import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  OutlinedInput,
  Chip,
  Grid,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useQuery, useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import moment from 'moment';
import { SimpleDatePicker } from 'components/form/FormInputDate';
import {
  ContactPersonReportFiltersInput,
  EHLBookingStatus,
  ECountryCode,
  ReportListHotelsQuery,
  ReportListPublicCompaniesQuery,
} from '__generated__/graphql';
import { allCountriesSorted } from 'languages';
import {
  REPORT_LIST_PUBLIC_COMPANIES_QUERY,
  REPORT_LIST_HOTEL_GROUPS_QUERY,
  REPORT_LIST_HOTELS_QUERY,
} from '../../gql';
import { LIST_CONTACT_SUBJECTS_QUERY } from '../../../clients/gql';
import { getContractTypeOptions } from '../utils/contractHelpers';

interface ContactPersonReportFiltersProps {
  filters: ContactPersonReportFiltersInput;
  onFilterChange: (field: keyof ContactPersonReportFiltersInput, value: any) => void;
}

const CONTRACT_TYPE_NONE = 'NONE';
const RELEVANT_CONTRACT_TYPES = ['SYMPOSION_HOTEL_CONTRACT', 'PARTNER_HOTEL_CONTRACT', 'MEETING_PACKAGE_IMPORT', 'FRIEND_HOTEL_CONTRACT', 'FRIEND_LOCATION_CONTRACT'];

export default function ContactPersonReportFilters({ filters, onFilterChange }: ContactPersonReportFiltersProps) {
  // Hotel groups
  const { data: hotelGroupsData, loading: hotelGroupsLoading } = useQuery(REPORT_LIST_HOTEL_GROUPS_QUERY);
  const hotelGroups = hotelGroupsData?.reportListHotelGroups || [];

  // Hotels (multiselect search)
  const [hotelSearchInput, setHotelSearchInput] = useState('');
  const [selectedHotels, setSelectedHotels] = useState<ReportListHotelsQuery['reportListHotels']>([]);
  const [runListHotels, { data: hotelsData, loading: hotelsLoading }] = useLazyQuery(REPORT_LIST_HOTELS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const allHotels = hotelsData?.reportListHotels || [];

  const hotelOptions = React.useMemo(() => {
    if (selectedHotels.length === 0) return allHotels;
    const selectedIds = selectedHotels.map((h) => h.id);
    return [...selectedHotels, ...allHotels.filter((h) => !selectedIds.includes(h.id))];
  }, [allHotels, selectedHotels]);

  useEffect(() => {
    if (filters.hotelIds?.length && selectedHotels.length === 0) {
      const found = allHotels.filter((h) => filters.hotelIds?.includes(h.id));
      if (found.length > 0) setSelectedHotels(found);
    } else if (!filters.hotelIds?.length && selectedHotels.length > 0) {
      setSelectedHotels([]);
      setHotelSearchInput('');
    }
  }, [filters.hotelIds, allHotels, selectedHotels.length]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (hotelSearchInput && hotelSearchInput.length >= 3) {
        runListHotels({ variables: { name: hotelSearchInput } });
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [hotelSearchInput, runListHotels]);

  // Companies (single search)
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<ReportListPublicCompaniesQuery['reportListPublicCompanies'][number] | null>(null);
  const [runListCompanies, { data: companiesData, loading: companiesLoading }] = useLazyQuery(REPORT_LIST_PUBLIC_COMPANIES_QUERY, {
    fetchPolicy: 'network-only',
  });
  const allCompanies = companiesData?.reportListPublicCompanies || [];

  const companyOptions = React.useMemo(() => {
    if (!selectedCompany) return allCompanies;
    return allCompanies.some((c) => c.id === selectedCompany.id)
      ? allCompanies
      : [selectedCompany, ...allCompanies];
  }, [allCompanies, selectedCompany]);

  useEffect(() => {
    if (filters.companyIds?.length && !selectedCompany) {
      const found = allCompanies.find((c) => c.id === filters.companyIds![0]);
      if (found) {
        setSelectedCompany(found);
        setCompanySearchInput(found.name || '');
      }
    } else if (!filters.companyIds?.length && selectedCompany) {
      setSelectedCompany(null);
      setCompanySearchInput('');
    }
  }, [filters.companyIds, allCompanies, selectedCompany]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (companySearchInput && companySearchInput.length >= 3) {
        runListCompanies({ variables: { name: companySearchInput } });
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [companySearchInput, runListCompanies]);

  // Contact subjects (multiselect)
  const { data: contactSubjectsData } = useQuery(LIST_CONTACT_SUBJECTS_QUERY, {
    variables: { where: { isActive: true } },
  });
  const contactSubjects = contactSubjectsData?.listContactSubjects || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={1.5}>
        {/* Firma */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={companyOptions}
            getOptionLabel={(option) => option.name || ''}
            value={selectedCompany}
            inputValue={companySearchInput}
            onInputChange={(_, newVal, reason) => {
              if (reason === 'input') {
                setCompanySearchInput(newVal);
                if (!newVal) {
                  onFilterChange('companyIds', undefined);
                  setSelectedCompany(null);
                }
              }
            }}
            onChange={(_, newValue) => {
              setSelectedCompany(newValue);
              onFilterChange('companyIds', newValue ? [newValue.id] : undefined);
              if (newValue) setCompanySearchInput(newValue.name || '');
              else setCompanySearchInput('');
            }}
            loading={companiesLoading}
            renderInput={(params) => (
              <TextField {...params} label={String(i18next.t('report-filter-company-min-3'))} placeholder={String(i18next.t('filter-company-search-placeholder'))} />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={companySearchInput.length < 3 ? String(i18next.t('filter-enter-min-3')) : String(i18next.t('filter-companies-not-found'))}
            clearText={String(i18next.t('delete'))}
          />
        </Grid>

        {/* Kundentyp */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-customer-type')}</InputLabel>
            <Select
              label={i18next.t('filter-customer-type')}
              value={filters.clientIsPremium === undefined || filters.clientIsPremium === null ? '' : filters.clientIsPremium ? 'premium' : 'classic'}
              onChange={(e) =>
                onFilterChange('clientIsPremium', e.target.value === 'premium' ? true : e.target.value === 'classic' ? false : undefined)
              }
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              <MenuItem value="classic">{i18next.t('filter-customer-type-classic')}</MenuItem>
              <MenuItem value="premium">{i18next.t('filter-customer-type-premium')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Hotels (multiselect) */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            multiple
            size="small"
            options={hotelOptions}
            getOptionLabel={(option) =>
              [option.name, option.businessZip, option.businessCity].filter(Boolean).join(', ')
            }
            value={selectedHotels}
            onInputChange={(_, newVal, reason) => {
              if (reason === 'input') setHotelSearchInput(newVal);
            }}
            onChange={(_, newValue) => {
              setSelectedHotels(newValue);
              onFilterChange('hotelIds', newValue.length ? newValue.map((h) => h.id) : undefined);
            }}
            loading={hotelsLoading}
            renderInput={(params) => (
              <TextField {...params} label={String(i18next.t('report-filter-hotels-min-3'))} placeholder={String(i18next.t('filter-hotel-search-placeholder'))} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
              ))
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={hotelSearchInput.length < 3 ? String(i18next.t('filter-enter-min-3')) : String(i18next.t('filter-hotels-not-found'))}
          />
        </Grid>

        {/* Hotelkette */}
        <Grid item xs={6} sm={3} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>{i18next.t('filter-hotel-group')}</InputLabel>
              <Select
                multiple
                label={i18next.t('filter-hotel-group')}
                value={filters.hotelGroupIds || []}
                onChange={(e) =>
                  onFilterChange('hotelGroupIds', typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : (e.target.value as number[]).length ? e.target.value : undefined)
                }
                input={<OutlinedInput label={i18next.t('filter-hotel-group')} />}
                disabled={hotelGroupsLoading}
                displayEmpty
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((id) => (
                      <Chip key={id} label={hotelGroups.find((g) => g.id === id)?.name || ''} size="small" />
                    ))}
                  </Box>
                )}
                notched
              >
                {hotelGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!!filters.hotelGroupIds?.length && (
              <IconButton size="small" onClick={() => onFilterChange('hotelGroupIds', undefined)} sx={{ mt: 1 }}>
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Grid>

        {/* Vereinbarungstyp */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-agreement-type')}</InputLabel>
            <Select
              label={i18next.t('filter-agreement-type')}
              value={filters.hotelContractType || ''}
              onChange={(e) => onFilterChange('hotelContractType', e.target.value || undefined)}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              <MenuItem value={CONTRACT_TYPE_NONE}>{i18next.t('report-contract-none')}</MenuItem>
              {getContractTypeOptions()
                .filter((o) => RELEVANT_CONTRACT_TYPES.includes(o.value))
                .map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Hotelland */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-country-hotel')}</InputLabel>
            <Select
              label={i18next.t('filter-country-hotel')}
              value={filters.hotelCountry || ''}
              onChange={(e) => onFilterChange('hotelCountry', (e.target.value as ECountryCode) || undefined)}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {allCountriesSorted.map(
                (country) =>
                  country && (
                    <MenuItem key={country.code} value={country.code}>
                      {country.label}
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
        </Grid>

        {/* Buchungsstatus */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-booking-state')}</InputLabel>
            <Select
              label={i18next.t('filter-booking-state')}
              value={filters.bookingStatus || ''}
              onChange={(e) => onFilterChange('bookingStatus', (e.target.value as EHLBookingStatus) || undefined)}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {Object.values(EHLBookingStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {i18next.t(`enums-EHLBookingStatus-${status}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Land (Kontaktperson) */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-contact-person-country')}</InputLabel>
            <Select
              label={i18next.t('filter-contact-person-country')}
              value={filters.contactPersonCountry || ''}
              onChange={(e) => onFilterChange('contactPersonCountry', (e.target.value as ECountryCode) || undefined)}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {allCountriesSorted.map(
                (country) =>
                  country && (
                    <MenuItem key={country.code} value={country.code}>
                      {country.label}
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
        </Grid>

        {/* Newsletter */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-newsletter')}</InputLabel>
            <Select
              label={i18next.t('filter-newsletter')}
              value={filters.newsletter === undefined || filters.newsletter === null ? '' : filters.newsletter ? 'yes' : 'no'}
              onChange={(e) =>
                onFilterChange('newsletter', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined)
              }
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              <MenuItem value="yes">{i18next.t('filter-cancellation-yes')}</MenuItem>
              <MenuItem value="no">{i18next.t('filter-cancellation-no')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Status Kontaktperson */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-contact-is-active')}</InputLabel>
            <Select
              label={i18next.t('filter-contact-is-active')}
              value={filters.isActive === undefined || filters.isActive === null ? '' : filters.isActive ? 'active' : 'inactive'}
              onChange={(e) =>
                onFilterChange('isActive', e.target.value === 'active' ? true : e.target.value === 'inactive' ? false : undefined)
              }
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              <MenuItem value="active">{i18next.t('active')}</MenuItem>
              <MenuItem value="inactive">{i18next.t('inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Erstellt von/bis */}
        <Grid item xs={6} sm={3} md={2}>
          <SimpleDatePicker
            label={i18next.t('filter-contact-created-from')}
            value={filters.createdFrom || null}
            onChange={(value) => onFilterChange('createdFrom', value)}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <SimpleDatePicker
            label={i18next.t('filter-contact-created-to')}
            value={filters.createdTo || null}
            onChange={(value) => onFilterChange('createdTo', value)}
          />
        </Grid>

        {/* Seminardatum von/bis */}
        <Grid item xs={6} sm={3} md={2}>
          <SimpleDatePicker
            label={i18next.t('filter-booking-date-from')}
            value={filters.bookingDateFrom ? new Date(filters.bookingDateFrom) : null}
            onChange={(value) => onFilterChange('bookingDateFrom', value ? moment(value).format('YYYY-MM-DD') : null)}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <SimpleDatePicker
            label={i18next.t('filter-booking-date-to')}
            value={filters.bookingDateTo ? new Date(filters.bookingDateTo) : null}
            onChange={(value) => onFilterChange('bookingDateTo', value ? moment(value).format('YYYY-MM-DD') : null)}
          />
        </Grid>

        {/* Anfragedatum von/bis */}
        <Grid item xs={6} sm={3} md={2}>
          <SimpleDatePicker
            label={i18next.t('filter-from')}
            value={filters.requestDateFrom || null}
            onChange={(value) => onFilterChange('requestDateFrom', value)}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <SimpleDatePicker
            label={i18next.t('filter-to')}
            value={filters.requestDateTo || null}
            onChange={(value) => onFilterChange('requestDateTo', value)}
          />
        </Grid>

        {/* Betreff(e) */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{i18next.t('filter-contact-subjects')}</InputLabel>
            <Select
              multiple
              label={i18next.t('filter-contact-subjects')}
              value={filters.subjectIds || []}
              onChange={(e) => {
                const val = typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : (e.target.value as number[]);
                onFilterChange('subjectIds', val.length ? val : undefined);
              }}
              input={<OutlinedInput label={i18next.t('filter-contact-subjects')} />}
              displayEmpty
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => (
                    <Chip key={id} label={contactSubjects.find((s) => s.id === id)?.name || `ID ${id}`} size="small" />
                  ))}
                </Box>
              )}
              notched
            >
              {contactSubjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}
